import type { JWTPayload } from 'jose';
import { extractToken, getCookie, getActiveClient } from './modules/cookie';
import { importWithBrand } from './utils/importWithBrand';
import type { Slots as LoginWallOverlaySlots } from './modules/login-wall/LoginWallOverlay.preprocess';
import type { Slots as IdentityWallOverlaySlots } from './modules/identity-wall/IdentityWallOverlay.preprocess';
import type { AnalyticsOptions } from './utils/analytics';
import { Events, generateEventName, getEmitter } from './utils/emitter';
import { registerPageVisibilityListener } from './utils/pageVisibilityListener';
import { SessionManager } from './modules/session-manager/SessionManager';

/**
 * The id token is a JWT token that contains information about the user.
 */
export interface IdToken extends JWTPayload {
  isInFirstTimeLoginBrand: boolean;
  'https://mediahuis.com/principal-type': 'USER';
  given_name: string;
  family_name: string;
  nickname: string;
  name: string;
  picture: string;
  gender: {
    type: 'Male' | 'Female' | 'Other';
  };
  updated_at: string;
  email: string;
  email_verified: boolean;
  sid?: string;
}

/**
 * Uniquely identifies an entitlement at its source system.
 */
export interface EntitlementId {
  process: string;
  system: string;
  sourceId: string;
}

/**
 * A single entitlement as emitted in the `https://mediahuis.com/entitlements` claim.
 * Only claims and roles active for the current brand are included.
 */
export interface Entitlement {
  id: EntitlementId;
  /** Active access claims for the brand, formatted as `${brand}:${code}` (e.g. `"hd:premium"`). */
  claims: Array<string>;
  roles: Array<string>;
}

/**
 * The access token is a JWT token that contains access information about the user.
 */
export interface AccessToken extends JWTPayload {
  'https://mediahuis.com/principal-type': 'USER';
  'https://mediahuis.com/subscribed-accesses'?: Array<string>;
  'https://mediahuis.com/entitlements'?: Array<Entitlement>;
  'https://mediahuis.com/identity-levels'?: Array<string>;
  iss: string;
  sub: string;
  aud: Array<string>;
  iat: number;
  exp: number;
  azp: string;
  scope: string;
}

export interface FullIdentity {
  email: string;
  emailVerified: boolean;
  namesPerson: {
    nickName: string | null;
    firstName: string | null;
    lastName: string | null;
  };
  demographicsPerson: {
    genderCode: 'u' | 'm' | 'f' | 'o' | null;
    genderCustom: string | null;
    dateOfBirth: string | null;
  };
  contactpointsPhone: {
    phone: string | null;
  };
  address: {
    street: string | null;
    houseNumber: string | null;
    houseNumberExtension: string | null;
    city: string | null;
    postalCode: string | null;
    countryCode: string | null;
    qualityLabel: string | null;
  };
  identityLevels: Array<string> | null;
}

interface Config {
  env: string;
  brand: string;
  version: string;
  prefix?: string;
}

// Explicit clientId wins (still needed to disambiguate multiple clients on one host); otherwise
// resolve from the `ciam_active_client` cookie so client apps needn't know their client id.
function resolveClientId(clientId?: string): string {
  if (clientId) return clientId;
  const active = getActiveClient();
  if (active?.clientId) return active.clientId;
  throw new Error(
    'CIAM: no clientId provided and no active client cookie found. Pass a clientId explicitly, ' +
      'or make sure the page is served through the CIAM proxy worker.',
  );
}

/**
 * Get the client configuration from cookies.
 *
 * @returns {Config}
 */
export function getConfig(clientId?: string): Config {
  const id = resolveClientId(clientId);
  const cookieValue = getCookie(`${id}_config`);
  if (!cookieValue) {
    throw new Error(`No config found for client id: ${id}`);
  }
  const params = new URLSearchParams(cookieValue);

  return (Array.from(params.entries()) as Array<[keyof Config, Config[keyof Config]]>).reduce((acc, [key, value]) => {
    acc[key] = value;

    return acc;
  }, {} as Config);
}

/**
 * Get the access token of a logged in user. Useful when you need to send this token to a backend service from the client.
 *
 * @returns Access token or null
 */
export function getAccessToken(clientId?: string) {
  return getCookie(`${resolveClientId(clientId)}_acc_token`);
}

/**
 * Returns all subscribed access claims.
 *
 * @deprecated Use {@link getUserEntitlements} instead. The `subscribed-accesses` claim is being
 * superseded by the richer `entitlements` claim and will be removed from the access token.
 * @returns The list of claims
 */
export async function getUserSubscriptions(clientId?: string) {
  const decodedAccessToken = await getDecodedAccessToken(clientId);

  return decodedAccessToken?.['https://mediahuis.com/subscribed-accesses'] ?? [];
}

/**
 * Returns the user's entitlements as read from the access token's `entitlements` claim.
 * Each entitlement lists the active access claims and roles for the current brand.
 *
 * @returns The list of entitlements, or an empty array when none are present
 */
export async function getUserEntitlements(clientId?: string): Promise<Array<Entitlement>> {
  const decodedAccessToken = await getDecodedAccessToken(clientId);

  return decodedAccessToken?.['https://mediahuis.com/entitlements'] ?? [];
}

/**
 * Returns the access token fully parsed and decoded of a loggedin user.
 *
 * @returns The parsed access token or null
 */
export async function getDecodedAccessToken(clientId?: string): Promise<AccessToken> {
  return extractToken<AccessToken>(`${resolveClientId(clientId)}_acc_token`);
}

/**
 * Retrieve the raw id token of a logged in user. If you're looking to extract this infomration you can simply use the `getUserInfo` function instead.
 *
 * @returns Raw JWT id token or null
 */
export function getIdToken(clientId?: string) {
  return getCookie(`${resolveClientId(clientId)}_id_token`);
}

/**
 * Returns the id token fully parsed and decoded of a loggedin user.
 *
 * @returns The parsed id token or null
 */
export async function getDecodedIdToken(clientId?: string): Promise<IdToken> {
  return extractToken<IdToken>(`${resolveClientId(clientId)}_id_token`);
}

/**
 * Returns the id token fully parsed and decoded of a loggedin user.
 *
 * @returns The parsed id token or null
 */
export async function getUserInfo(clientId?: string): Promise<IdToken> {
  return getDecodedIdToken(clientId);
}

/**
 * Returns the id token fully parsed and decoded of a loggedin user.
 *
 * @param clientId Auth0 client id
 * @returns The parsed id token or null
 */
export async function getFullUserProfile(clientId?: string): Promise<FullIdentity | null> {
  const id = resolveClientId(clientId);
  if (!isAuthenticated(id)) {
    return null;
  }

  const accessToken = await getDecodedAccessToken(id);

  const config = getConfig(id);
  let domain = `https://identity-management.mediahuis.com`;
  if (config.env === 'test') {
    domain = `https://identity-management-tst.mediahuis.com`;
  } else if (config.env === 'preview' || config.env === 'staging') {
    domain = `https://identity-management-uat.mediahuis.com`;
  } else if (config.env === 'dev') {
    domain = `https://identity-management-dev.mediahuis.com`;
  }
  const url = new URL(`/v4/api/identities/${accessToken.sub}`, domain);
  const response = await fetch(url, {
    headers: {
      authorization: `Bearer ${getAccessToken(id)}`,
    },
  });

  if (!response.ok) {
    return null;
  }

  return response.json();
}

/**
 * Refreshes the access token of a logged in user.
 *
 * @returns True if the token was succesfully refreshed, false otherwise
 */
export async function refreshAccessToken(clientId?: string): Promise<boolean> {
  const basePath = getBasePath(resolveClientId(clientId));

  try {
    const response = await fetch(`${basePath}/auth/refresh`, {
      method: 'POST',
    });
    if (!response.ok) {
      return false;
    }

    const { ok } = await response.json();

    return ok;
  } catch (_err) {
    return false;
  }
}

/**
 * Attach a listener to an event.
 *
 * @param event the name of the event to listen to
 * @param callback your callback function
 */
export function on(clientId: string | undefined, event: Events, callback: () => void): void {
  const id = resolveClientId(clientId);
  // if someone is listening we can capture the clientId and start listening for expired tokens
  let isUserAuthenticated = isAuthenticated(id);
  let currentAccessToken = getAccessToken(id);

  registerPageVisibilityListener(async (isVisible) => {
    if (isVisible) {
      if (isUserAuthenticated) {
        // still authenticated but access token has changed
        if (isAuthenticated(id) && currentAccessToken !== getAccessToken(id)) {
          getEmitter().emit(generateEventName('auth:session_changed', id));
          currentAccessToken = getAccessToken(id);
          return;
        }

        const accessToken = await getDecodedAccessToken(id);
        // if expired
        const tokenExpirationSkew = 300_000;
        if (!isAuthenticated(id) || accessToken?.exp * 1000 <= Date.now() + tokenExpirationSkew /*5min*/) {
          const isRefreshed = await refreshAccessToken(id);
          isUserAuthenticated = isAuthenticated(id);
          if (isRefreshed) {
            currentAccessToken = getAccessToken(id);
            getEmitter().emit(generateEventName('auth:session_changed', id));
          } else {
            getEmitter().emit(generateEventName('auth:session_expired', id));
          }
        }
      } else {
        if (isAuthenticated(id)) {
          getEmitter().emit(generateEventName('auth:session_changed', id));
          currentAccessToken = getAccessToken(id);
          isUserAuthenticated = true;
        }
      }
    }
  });

  getEmitter().on(generateEventName(event, id), callback);
}

/**
 * Detach a listener to an event.
 *
 * @param event the name of the event to listen to
 * @param callback your callback function
 */
export function off(clientId: string | undefined, event: Events, callback: () => void): void {
  getEmitter().off(generateEventName(event, resolveClientId(clientId)), callback);
}

/**
 * Is the user authenticated?
 *
 * @returns True if the user is authenticated, false otherwise
 */
export function isAuthenticated(clientId?: string): boolean {
  const id = resolveClientId(clientId);
  return !!getAccessToken(id) && !!getIdToken(id);
}

/**
 * Is the email verified?
 *
 * @returns True if the email is verified, false otherwise
 */
export async function isEmailVerified(clientId?: string): Promise<boolean> {
  const userInfo = await getUserInfo(clientId);

  if (!userInfo) {
    return false;
  }

  return userInfo.email_verified;
}

function getBasePath(clientId?: string) {
  const config = getConfig(clientId);
  return config.prefix === '/' ? '' : config.prefix;
}

export async function hasIdentityLevel(clientId: string | undefined, identityLevel: string): Promise<boolean> {
  const accessToken = await getDecodedAccessToken(clientId);

  return accessToken['https://mediahuis.com/identity-levels']?.includes(identityLevel) ?? false;
}

export async function sendEmailVerification(clientId?: string): Promise<boolean> {
  const id = resolveClientId(clientId);
  const userInfo = await getDecodedIdToken(id);
  if (userInfo.email_verified) {
    return false;
  }

  const basePath = getBasePath(id);
  const verifyEmailUrl = new URL(`${basePath}/auth/verify-email`, window.location.origin);
  verifyEmailUrl.searchParams.append('accountId', userInfo.sub);

  try {
    const resp = await fetch(verifyEmailUrl, {
      headers: {
        authorization: 'Bearer ' + getAccessToken(id),
      },
    });

    return resp.ok;
  } catch (err) {
    return false;
  }
}

/**
 * When not loggedin show login wall.
 * showLoginWall let you put our shared component based on the Mediahuis design system in your page.
 *
 * @param options The list of options to pass to the popup
 * @param options.type The type of email confirmation to show
 * @param options.loginIdentityLevel The identity level to login
 * @param options.registerIdentityLevel The identity level to register
 * @param options.el The DOM element to render the email confirmation in
 * @param options.slots.title Custom title for login wall
 * @param options.slots.content Custom content for login wall
 * @param options.slots.callToAction Custom call to action for login wall
 * @param options.onTrackEvent Function to track events
 * @returns
 */
export async function showLoginWall({
  type,
  clientId,
  loginIdentityLevel,
  registerIdentityLevel,
  el,
  slots = {},
  options,
  onTrackEvent,
}: {
  type: 'paywall' | 'inline' | 'overlay';
  clientId?: string;
  loginIdentityLevel: string;
  registerIdentityLevel: string;
  el: HTMLElement;
  slots?: Partial<LoginWallOverlaySlots>;
  options: Partial<AnalyticsOptions>;
  onTrackEvent: (event: any) => void;
}): Promise<void> {
  const id = resolveClientId(clientId);
  if (!['paywall', 'inline', 'overlay'].includes(type)) {
    throw new Error(`Unsupported type: ${type}, only paywall, inline or overlay is currently implemented.`);
  }

  if (!el) {
    throw new Error(`No element provided to render the identity wall in.`);
  }

  if (isAuthenticated(id)) {
    throw new Error(`User is authenticated.`);
  }

  const config = getConfig(id);

  if (type === 'overlay') {
    const { appendLoginWall } = await importWithBrand('./modules/login-wall/LoginWallOverlay', config.brand);

    return appendLoginWall(el, {
      requiredLoginIdentityLevel: loginIdentityLevel,
      requiredRegisterIdentityLevel: registerIdentityLevel,
      brand: config.brand,
      slots,
      onTrackEvent,
      options,
    });
  } else {
    const { appendLoginWall } = await importWithBrand('./modules/login-wall/LoginWall', config.brand);

    return appendLoginWall(el, {
      requiredLoginIdentityLevel: loginIdentityLevel,
      requiredRegisterIdentityLevel: registerIdentityLevel,
      brand: config.brand,
      clientId: id,
      slots,
      onTrackEvent,
      options,
    });
  }
}

/**
 * Some brands require a specific identity level before reading any metered or plus articles.
 * showIdentityWall let you put our shared component based on the Mediahuis design system in your page.
 *
 * @param options The list of options to pass to the popup
 * @param options.clientId Auth0 client id
 * @param options.type The type of email confirmation to show
 * @param options.requiredIdentityLevel The identity level to progress
 * @param options.el The DOM element to render the email confirmation in
 * @param options.slots.title Custom title for login wall
 * @param options.slots.content Custom content for login wall
 * @param options.slots.callToAction Custom call to action for login wall
 * @param options.onTrackEvent Function to track events
 * @param options.options["ext-internal"] Internal tracking parameter
 * @returns
 */
export async function showIdentityWall({
  type,
  clientId,
  identityLevel,
  el,
  slots = {},
  onTrackEvent,
  options,
}: {
  type: 'paywall' | 'overlay' | 'inline';
  clientId?: string;
  identityLevel: string;
  el: HTMLElement;
  slots?: Partial<IdentityWallOverlaySlots>;
  onTrackEvent: (event: Event) => void;
  options: Partial<AnalyticsOptions>;
}): Promise<void> {
  // if (type !== 'paywall') {
  // 	throw new Error(`Unsupported type: ${type}, only paywall is currently implemented.`);
  // }

  const id = resolveClientId(clientId);

  if (!el) {
    throw new Error(`No element provided to render the identity wall in.`);
  }

  if (!isAuthenticated(id)) {
    throw new Error(`User is not authenticated.`);
  }

  const config = getConfig(id);

  if (type === 'overlay') {
    const { appendIdentityWall } = await importWithBrand('./modules/identity-wall/IdentityWallOverlay', config.brand);

    return appendIdentityWall(el, {
      requiredIdentityLevel: identityLevel,
      brand: config.brand,
      clientId: id,
      slots,
      onTrackEvent,
      options,
    });
  } else {
    const { appendIdentityWall } = await importWithBrand('./modules/identity-wall/IdentityWall', config.brand);

    return appendIdentityWall(el, {
      requiredIdentityLevel: identityLevel,
      brand: config.brand,
      clientId: id,
      slots,
      onTrackEvent,
      options,
    });
  }
}

/**
 * Some brands require a specific identity to be show  before reading any metered or plus articles.
 *
 * @param options The list of options to pass to the popup
 * @param options.clientId Auth0 client id
 * @param options.type The type of email confirmation to show
 * @param options.el The DOM element to render the email confirmation in
 * @param options.onTrackEvent Function to track events
 * @returns
 */
export async function showEmailConfirmation({
  type,
  clientId,
  el,
  onTrackEvent,
}: {
  type: 'paywall' | 'inline';
  clientId?: string;
  el: HTMLElement;
  onTrackEvent: (event: Event) => void;
}) {
  const id = resolveClientId(clientId);
  if (type !== 'paywall' && type !== 'inline') {
    throw new Error(`Unsupported type: ${type}, only paywall or inline is currently implemented.`);
  }

  if (!isAuthenticated(id)) {
    throw new Error(`User is not authenticated.`);
  }

  if (!el) {
    throw new Error(`No element provided to render the email confirmation box in.`);
  }

  const userInfo = await getDecodedIdToken(id);

  if (userInfo.email_verified) {
    throw new Error(`User has already been verified.`);
  }

  const config = getConfig(id);

  const { appendEmailVerificationWall } = await importWithBrand('./modules/email-confirmation/EmailConfirmationWall', config.brand);
  return appendEmailVerificationWall(el, { email: userInfo.email, brand: config.brand, clientId: id, onTrackEvent });
}

/**
 * If you want to control when to show the silent login popup.
 *
 * Note that we're using the Mediahuis design system to create these popups.
 *
 * @param options The list of options to pass to the popup
 * @param options.clientId Auth0 client id
 * @param options.el The DOM element to render the silent login notification in
 * @returns
 */
export async function showSilentLoginNotification({
  clientId,
  el,
  language,
}: {
  clientId?: string;
  el: HTMLElement;
  language?: string;
}): Promise<void> {
  const id = resolveClientId(clientId);
  const parsedUrl = new URL(window.location.href);
  parsedUrl.searchParams.delete('_sl');
  parsedUrl.searchParams.delete('_sl_ui_locales');
  window.history.replaceState(history.state, undefined, parsedUrl.toString());
  const userInfo = await getUserInfo(id);

  if (!userInfo.email) {
    return;
  }

  if (!el) {
    throw new Error(`No element provided to render the silent login toast in.`);
  }

  const { appendNotificationToast } = await importWithBrand('./modules/silent-login/NotificationToast', 'mh');

  Object.assign(el.style, {
    position: 'fixed',
    zIndex: '9999',
    top: 0,
    right: 0,
  });

  const config = getConfig(id);
  await appendNotificationToast(el, { email: userInfo.email, brand: config.brand, language });

  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      resolve();
    });
  });
}

/**
 * Options for specifying identity levels required for login and registration.
 *
 * @param loginIdentity The identity level required for login.
 * @param registerIdentity The identity level required for registration.
 */
type IdentityLevelOptions = {
  loginIdentity: string;
  registerIdentity: string;
};

/**
 * General options for constructing URLs and handling authentication flows.
 *
 * @param returnTo The URL to redirect to after login.
 * @param login The base login URL. Defaults to '/auth/login'.
 * @param connection The name of the social connection (eg. 'mh-google-social' for Google).
 * @param skipEmailVerification Whether to skip email verification. Defaults to false.
 * @param brand The brand according to which CIAM should behave. !Only use if your client has to support multiple "brands", else configure this through the client config.
 * @param language The language to be used by authentication screens. If not provided, default configured in the cloudflare worker is used
 */
type GeneralOptions = {
  returnTo?: string;
  login?: string;
  connection?: string;
  skipEmailVerification?: boolean;
  brand?: string;
  language?: Language;
};

export enum Language {
  NL = 'nl',
  FR = 'fr-FR',
  DE = 'de',
  EN = 'en',
  PT = 'pt-PT',
}

/**
 * Type definition for analytics parameters.
 * These parameters are used for tracking and analytics purposes.
 *
 * @param articleId Article ID.
 * @param prsid PRS ID.
 * @param cid CID.
 * @param internal Internal tracking parameter.
 */
type TrackingOptions = {
  articleId?: string;
  prsid?: string;
  cid?: string;
  internal?: string;
};

/**
 * Constructs the login URL with the specified parameters.
 *
 * @param clientId - The Auth0 client id.
 * @param identityLevel The identity level options required for login and registration.
 * @param identityLevel.loginIdentity The identity level required for login.
 * @param identityLevel.registerIdentity The identity level required for registration.
 * @param options - General options for constructing the URL.
 * @param options.skipEmailVerification - Whether to skip email verification. Defaults to false.
 * @param options.connection - The name of the social connection (e.g., 'mh-google-social' for Google).
 * @param options.returnTo - The URL to redirect to after login. Defaults to the current window location.
 * @param options.brand - The brand according to which CIAM should behave. !Only use if your client has to support multiple "brands", else configure this through the client config.
 * @param options.language - The language to be used by authentication screens. If not provided, default configured in cloudflare worker is used
 * @param tracking - Tracking options for analytics purposes.
 * @param tracking.articleId - Article ID for tracking.
 * @param tracking.prsid - PRS ID for tracking.
 * @param tracking.cid - CID for tracking.
 * @param tracking.internal - Internal tracking parameter.
 * @returns A promise that resolves to the constructed login URL as a string.
 */
export async function getLoginUrl({
  clientId,
  identityLevel,
  options,
  tracking,
}: {
  clientId?: string;
  identityLevel: IdentityLevelOptions;
  options: GeneralOptions;
  tracking: TrackingOptions;
}): Promise<string> {
  return await buildUrl({
    identityLevel: identityLevel,
    options: options,
    tracking: tracking,
    clientId: clientId,
    authType: 'login',
  });
}

/**
 * Constructs the register URL with the specified parameters.
 *
 * @param clientId - The Auth0 client id.
 * @param identityLevel The identity level options required for login and registration.
 * @param identityLevel.loginIdentity The identity level required for login.
 * @param identityLevel.registerIdentity The identity level required for registration.
 * @param options - General options for constructing the URL.
 * @param options.skipEmailVerification - Whether to skip email verification. Defaults to false.
 * @param options.connection - The name of the social connection (e.g., 'mh-google-social' for Google).
 * @param options.returnTo - The URL to redirect to after registration. Defaults to the current window location.
 * @param options.brand The brand according to which CIAM should behave. !Only use if your client has to support multiple "brands", else configure this through the client config.
 * @param options.language The language to be used by authentication screens. If not provided, default configured in cloudflare worker is used
 * @param tracking - Tracking options for analytics purposes.
 * @param tracking.articleId - Article ID for tracking.
 * @param tracking.prsid - PRS ID for tracking.
 * @param tracking.cid - CID for tracking.
 * @param tracking.internal - Internal tracking parameter.
 * @returns A promise that resolves to the constructed register URL as a string.
 */
export async function getRegisterUrl({
  clientId,
  identityLevel,
  options,
  tracking,
}: {
  clientId?: string;
  identityLevel: IdentityLevelOptions;
  options: GeneralOptions;
  tracking: TrackingOptions;
}): Promise<string> {
  return await buildUrl({
    identityLevel: identityLevel,
    options: options,
    tracking: tracking,
    clientId: clientId,
    authType: 'register',
  });
}

/**
 * Constructs a URL with the specified parameters for either login or registration.
 *
 * @param searchParams - The URL search parameters to be appended.
 * @param options - General options for constructing the URL.
 * @param options.skipEmailVerification - Whether to skip email verification. Defaults to false.
 * @param options.connection - The name of the social connection (e.g., 'mh-google-social' for Google).
 * @param options.returnTo - The URL to redirect to after the operation. Defaults to the current window location.
 * @param options.brand - The brand according to which CIAM should behave. !Only use if your client has to support multiple "brands", else configure this through the client config.
 * @param options.language - The language to be used by authentication screens. If not provided, default configured in ??? is used
 * @param tracking - Tracking options for analytics purposes.
 * @param tracking.articleId - Article ID for tracking.
 * @param tracking.prsid - PRS ID for tracking.
 * @param tracking.cid - CID for tracking.
 * @param tracking.internal - Internal tracking parameter.
 * @param clientId - The Auth0 client id.
 * @param authType - The type of authentication ('login' or 'register').
 * @returns A promise that resolves to the constructed URL as a string.
 */
async function buildUrl({
  identityLevel,
  options: { skipEmailVerification = false, connection, returnTo = window.location.href, brand, language },
  tracking,
  clientId,
  authType,
}: {
  identityLevel: IdentityLevelOptions;
  options: GeneralOptions;
  tracking: TrackingOptions;
  clientId?: string;
  authType: 'login' | 'register';
}): Promise<string> {
  const searchParams = new URLSearchParams();
  const config = getConfig(clientId);
  if (identityLevel.loginIdentity) {
    searchParams.append('login_identity_level', identityLevel.loginIdentity);
  }
  if (identityLevel.registerIdentity) {
    searchParams.append('register_identity_level', identityLevel.registerIdentity);
  }
  searchParams.append('skip_email_verification', skipEmailVerification ? '1' : '0');
  if (connection) {
    searchParams.append('connection', connection);
  }
  if (returnTo) {
    searchParams.append('returnTo', returnTo);
  }
  if (tracking.prsid) {
    searchParams.append('ext-prsid', tracking.prsid);
  }
  if (tracking.cid) {
    searchParams.append('ext-cid', tracking.cid);
  }
  if (tracking.internal) {
    searchParams.append('ext-internal', tracking.internal);
  }
  if (tracking.articleId) {
    searchParams.append('ext-articleId', tracking.articleId);
  }
  if (language) {
    searchParams.append('ui_locales', language);
  }
  if (brand) {
    searchParams.append('ext-brand', brand);
  }
  let prefix = config.prefix ? config.prefix : '/';
  if (!prefix.endsWith('/')) {
    prefix += '/';
  }
  const resultUrl = new URL(`${window.location.origin}${prefix}auth/${authType}`);
  resultUrl.search = searchParams.toString();
  return resultUrl.toString();
}

let globalSessionManager: SessionManager | null = null;

/**
 * Session manager callback options
 */
export interface SessionManagerCallbacks {
  onSessionBlocked?: () => void;
  onSessionUnblocked?: () => void;
}

/**
 * Initialize and connect to the global session manager.
 * @param clientId The clientID of the application
 * @param callbacks Optional callback functions for session events
 * @returns Promise<SessionManager>
 */
export async function initializeSessionManager(clientId: string | undefined, callbacks?: SessionManagerCallbacks): Promise<SessionManager> {
  const id = resolveClientId(clientId);
  const config = getConfig(id);
  let sessionManagerEndpoint = 'session-management.mediahuis.com';

  if (config.env === 'dev') {
    sessionManagerEndpoint = 'session-management-dev.mediahuis.com';
  } else if (config.env === 'test') {
    sessionManagerEndpoint = 'session-management-tst.mediahuis.com';
  } else if (config.env === 'preview' || config.env === 'staging') {
    sessionManagerEndpoint = 'session-management-uat.mediahuis.com';
  }

  if (!globalSessionManager) {
    globalSessionManager = new SessionManager({ sessionManagerEndpoint, clientId: id });
  }

  if (callbacks?.onSessionBlocked) {
    getEmitter().on('session:blocked', callbacks.onSessionBlocked);
  }

  if (callbacks?.onSessionUnblocked) {
    getEmitter().on('session:unblocked', callbacks.onSessionUnblocked);
  }

  getEmitter().on(generateEventName('auth:session_changed', id), async () => {
    await globalSessionManager.shutdown();
    globalSessionManager = new SessionManager({ sessionManagerEndpoint, clientId: id });
    await globalSessionManager.connect();
  });

  getEmitter().on(generateEventName('auth:session_expired', id), async () => {
    await globalSessionManager.shutdown();
  });

  await globalSessionManager.connect();
  return globalSessionManager;
}
